import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  CloudUpload,
  CheckCircle,
  Error,
  FileDownload,
  Close,
  Refresh,
} from "@mui/icons-material";
import { Snackbar } from "@mui/material";
import { useImportCSVPreview, useImportCSVExecute, useDownloadCSVTemplate } from "../../hooks/queries/useImport";
import { useInstitutes } from "../../hooks/queries/useInstitutes";
import { useDepartments } from "../../hooks/queries/useDepartments";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import departmentService from "../../services/departmentService";
import api from "../../services/api";
import { useRolesForSelect } from "../../hooks/queries/useRBAC";

const DataImportWizard = ({ open, onClose, entityType, onImportComplete, instituteId }) => {
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [selectedErrorDetail, setSelectedErrorDetail] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [parentEntity, setParentEntity] = useState("");
  const [userType, setUserType] = useState("");
  const [localEntityType, setLocalEntityType] = useState(entityType || "");
  const [departmentId, setDepartmentId] = useState("");
  const [classId, setClassId] = useState("");
  const [practicalBatchCode, setPracticalBatchCode] = useState("");
  // teacher dropdown removed; keep a placeholder to avoid runtime ReferenceErrors
  const teacherId = "";

  // Map selected userType (role slug) to backend import entity type
  const mapUserTypeToEntityType = (type) => {
    const key = (type || "").toString().toLowerCase();
    if (key === "student" || key === "students") return "students";
    if (key === "superadmin" || key === "super_admin" || key === "superadmins") return "superadmins";
    // Default: treat all other roles (teacher, hod, admin, institute, custom staff) as teacher-like imports
    return "teachers";
  };

  // Calculate effective type early to avoid initialization issues
  const resolvedEntityKey = entityType || localEntityType;
  const effectiveType =
    resolvedEntityKey === "users"
      ? (userType ? mapUserTypeToEntityType(userType) : "")
      : resolvedEntityKey;

  // Determine current user role (defaults to institute admin)
  const currentUserRole = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return (u?.role || localStorage.getItem('userRole') || 'admin').toString().toLowerCase();
    } catch (e) {
      return (localStorage.getItem('userRole') || 'admin').toString().toLowerCase();
    }
  })();

  // Load roles dynamically for the User Type dropdown
  const { roles: roleOptions } = useRolesForSelect();

  // For non-superadmin callers, hide high-privilege roles from import UI
  const isSuperAdminUI = currentUserRole === "superadmin" || currentUserRole === "super_admin";
  const allowedUserTypeOptions = (roleOptions || [])
    .filter((r) => {
      const id = (r.id || "").toString().toLowerCase();
      if (!isSuperAdminUI) {
        // Match previous behavior: non-superadmin could see Teacher/HOD/Student only
        if (id === "superadmin" || id === "super_admin" || id === "admin") return false;
      }
      // Only show roles that map to a known import entity type
      const mapped = mapUserTypeToEntityType(id);
      return mapped === "teachers" || mapped === "students" || mapped === "superadmins";
    })
    .map((r) => ({
      value: (r.id || "").toString().toLowerCase(),
      label: r.label,
    }));

  // Determine current institute id from props or localStorage (for institute/admin users)
  const currentInstituteId = (() => {
    if (instituteId) return instituteId;
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      // support different shapes: user.instituteId or user.institute?.id
      return u?.instituteId || (u?.institute && u.institute.id) || null;
    } catch (e) {
      return null;
    }
  })();

  // React Query mutations
  const previewMutation = useImportCSVPreview();
  const importMutation = useImportCSVExecute();
  const downloadTemplateMutation = useDownloadCSVTemplate();
  
  // Fetch departments for selected institute
  const { data: departmentsResponse, isLoading: departmentsLoading, refetch: refetchDepartments } = useQuery({
    queryKey: ['departments', 'names', parentEntity],
    queryFn: () => departmentService.getDepartmentNames(parentEntity),
    enabled: !!parentEntity,
  });
  const departments = departmentsResponse?.data?.data || [];

  const { data: subjectSemestersFromApi = [], isLoading: subjectSemestersLoading } = useQuery({
    queryKey: ["import-subject-semesters", parentEntity],
    queryFn: async () => {
      const params = new URLSearchParams({ page: "1", limit: "200" });
      if (parentEntity) params.set("instituteId", String(parentEntity));
      const res = await api.get(`/academic-info/semester/all?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      return Array.isArray(data?.semesters) ? data.semesters : [];
    },
    enabled: open && effectiveType === "subjects" && !!parentEntity,
    // IMPORTANT: semesters can be added in another tab/screen (Academic Info).
    // Refetch on focus/mount so user doesn't need a hard refresh.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: "always",
  });

  // When dialog opens, force-refresh semesters (Academic Info can change them)
  useEffect(() => {
    if (!open) return;
    if (effectiveType !== "subjects") return;
    if (!parentEntity) return;
    queryClient.invalidateQueries({ queryKey: ["import-subject-semesters", parentEntity] });
    queryClient.refetchQueries({ queryKey: ["import-subject-semesters", parentEntity] });
  }, [open, effectiveType, parentEntity, queryClient]);

  const normalizeSemesterValue = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const allowedSemesterTokens = useMemo(() => {
    const tokens = new Set();
    (subjectSemestersFromApi || []).forEach((s) => {
      const semesterName = s?.semesterName ?? s?.name ?? s?.semester ?? "";
      const normalizedName = normalizeSemesterValue(semesterName);
      if (normalizedName) tokens.add(normalizedName);
    });
    return tokens;
  }, [subjectSemestersFromApi]);

  const validateSubjectSemesters = (rows = []) => {
    if (effectiveType !== "subjects") return { validRows: rows, invalidRows: [] };
    const validRows = [];
    const invalidRows = [];

    rows.forEach((row) => {
      const rawSemester = row?.semester ?? row?.Semester ?? row?.semesterName ?? "";
      const normalizedSemester = normalizeSemesterValue(rawSemester);
      const isValid = normalizedSemester && allowedSemesterTokens.has(normalizedSemester);

      if (isValid) {
        validRows.push(row);
      } else {
        invalidRows.push({
          row,
          reason: "Row: Semester: is not available in Academic Info.",
          errors: [
            {
              field: "semester",
              column: "Semester",
              message: "is not available in Academic Info.",
            },
          ],
        });
      }
    });

    return { validRows, invalidRows };
  };

  // Reset classId when departmentId changes
  useEffect(() => {
    setClassId("");
    setPracticalBatchCode("");
  }, [departmentId]);

  // Reset selected batch when class changes
  useEffect(() => {
    setPracticalBatchCode("");
  }, [classId]);

  // Reset departmentId and refetch departments when parentEntity changes
  useEffect(() => {
    setDepartmentId("");
    if (parentEntity) {
      refetchDepartments();
    }
  }, [parentEntity, refetchDepartments]);

  const steps = ["Upload File", "Preview & Validate", "Import"];
  const fileInputRef = useRef(null);
  const isFileProcessing = previewMutation.isPending || importMutation.isPending;
  const loading =
    isFileProcessing || downloadTemplateMutation.isPending;

  // Entity configuration
  const entityConfig = {
    institutes: {
      title: "Import Institutes",
      endpoint: "admin/import-institutes-from-csv",
      templateEndpoint: "/admin/download-institute-format-csv",
      requiresParent: false,
      // Backend requires only instituteName, email and phoneNumber for CSV import
      requiredFields: ["instituteName", "email", "phoneNumber"],
      optionalImportColumns: [
        "street",
        "city",
        "state",
        "country",
        "pinCode",
        "website",
        "latitude",
        "longitude",
        "instituteDistanceRadius",
        "teacherClassDistanceRadius",
      ],
      optionalImportHint:
        "Address fields (street, city, state, country, pinCode) are optional. Use the downloaded template for correct column headers.",
      acceptedFiles: ".csv,.xlsx,.xls",
    },
    departments: {
      title: "Import Departments",
      endpoint: "institute/import-departments-from-csv",
      templateEndpoint: "/institute/generate-department-csv-import-template-download",
      requiresParent: true,
      requiredFields: ["name", "code"],
      /** Same optional fields as Create/Edit department — backend accepts description, vision, mission */
      optionalImportColumns: ["description", "vision", "mission"],
      optionalImportHint:
        "Same as creating/editing a department. Alternate CSV headers such as Vision, Mission, Department Vision, or Department Mission are accepted.",
      parentType: "institute",
      acceptedFiles: ".csv,.xlsx,.xls",
    },
    teachers: {
      title: "Import Teachers",
      endpoint: "institute/import-teachers-from-csv",
      templateEndpoint: "/institute/generate-teacher-csv-import-template-download",
      requiresParent: true,
        // Align with Add New Teacher form: include DOB, gender, qualification, experienceYears, designation, salary, joiningDate
        requiredFields: [
          "firstName",
          "lastName",
          "email",
          "dob",
          "phoneNumber",
          "gender",
          "role",
          "qualification",
          "experienceYears",
          "designation",
          "salary",
          "departmentName",
          "joiningDate",
        ],
      parentType: "institute",
      acceptedFiles: ".csv,.xlsx,.xls",
    },
    classes: {
      title: "Import Classes",
      endpoint: "institute/import-class-csv",
      templateEndpoint: "/institute/download-class-csv-format",
      requiresParent: true,
      requiredFields: ["departmentName", "name", "section", "course", "teacher"],
      optionalImportHint:
        "Department comes from each CSV row (departmentName). You don't need to select Department in the popup.",
      parentType: "institute",
      acceptedFiles: ".csv,.xlsx,.xls",
    },
    courses: {
      title: "Import Courses",
      endpoint: "institute/import-courses-csv",
      // Use dedicated courses template that matches Add Course form
      templateEndpoint: "/institute/download-courses-csv-format",
      requiresParent: true,
      requiredFields: ["name", "code", "duration"],
      parentType: "institute",
      acceptedFiles: ".csv,.xlsx,.xls",
    },
    subjects: {
      title: "Import Subjects",
      endpoint: "institute/import-subjects-csv",
      templateEndpoint: "/institute/download-subjects-csv-format",
      requiresParent: true,
      requiredFields: [
        "name",
        "code",
        "departmentName",
        "course",
        "semester",
        "credits",
        "class",
        "teacher",
        "description",
      ],
      optionalImportHint:
        "Department comes from each CSV row (departmentName). You don't need to select Department in the popup.",
      parentType: "institute",
      acceptedFiles: ".csv,.xlsx,.xls",
    },
    students: {
      title: "Import Students",
      endpoint: "teacher/import-student-csv",
      templateEndpoint: "/teacher/download-teachers-csv-format",
      requiresParent: true,
      requiredFields: [
        "firstName",
        "lastName",
        "departmentName",
        "className",
        "email",
        "dob",
        "phoneNumber",
        "gender",
        "parentName",
        "parentPhone",
        "admissionDate",
        "startBatchDate",
        "endBatchDate",
      ],
      optionalImportColumns: [
        "section",
        "prnNumber",
        "rollNumber",
        "street",
        "city",
        "state",
        "country",
        "pinCode",
        "practicalBatchCode",
        "currentSemester",
        "currentYear",
      ],
      optionalImportHint:
        "Department and class come from each CSV row (departmentName, className; use section when multiple classes share the same name). Select institute above — no department/class dropdown here.",
      parentType: "institute",
      acceptedFiles: ".csv,.xlsx,.xls",
    },
    superadmins: {
      title: "Import Super Admins",
      endpoint: "super/admin/import-superadmins-from-csv",
      templateEndpoint: "/super/admin/download-superadmin-csv-import-template",
      requiresParent: false,
      requiredFields: ["email", "phoneNumber"],
      optionalImportColumns: ["firstName", "lastName", "fullName", "gender"],
      optionalImportHint: "Provide firstName + lastName, or fullName (split automatically).",
      acceptedFiles: ".csv,.xlsx,.xls",
    },
  };

  const config = entityConfig[effectiveType] || entityConfig[resolvedEntityKey] || entityConfig[entityType] || {};
  
  // Fetch institutes for dropdown (only when needed)
  const { data: institutesResp, isLoading: institutesLoading, error: institutesError } = useInstitutes({ limit: 99999 }, {
    enabled: config.requiresParent && !currentInstituteId && !instituteId
  });
  // normalize to array for dropdown compatibility with older callers
  const institutesRaw = Array.isArray(institutesResp)
    ? institutesResp
    : institutesResp?.data?.institutes || institutesResp?.data || institutesResp?.institutes || [];
  // Only show active institutes (status === true) in the dropdown
  const institutes = institutesRaw.filter((inst) => inst.status !== false);

  // Compute required fields to display, removing ones satisfied by dropdown selections
  const effectiveRequiredFields = React.useMemo(() => {
    const base = Array.isArray(config.requiredFields) ? [...config.requiredFields] : [];
    let out = base;
    if (parentEntity) {
      out = out.filter((f) => f !== 'instituteName' && f !== 'instituteId');
    }
    return out;
  }, [config.requiredFields, parentEntity]);

  useEffect(() => {
    if (open) {
      resetWizard();
    }
  }, [open]);

  const resetWizard = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setPreviewData(null);
    setImportResults(null);
    setError(null);
    setParentEntity(instituteId || currentInstituteId || "");
    setUserType("");
    setDepartmentId("");
    setClassId("");
    setPracticalBatchCode("");
    setLocalEntityType(entityType || "");
  };

  const handleFileSelect = async (event) => {
    if (isFileProcessing) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setError(null);
    try {
      await handlePreviewUpload(file);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handlePreviewUpload = async (file) => {
    setError(null);

    try {
      if (effectiveType === "subjects") {
        if (!parentEntity) {
          const msg = "Please select an Institute before uploading subjects.";
          setError(msg);
          setSnackbar({ open: true, message: msg, severity: "warning" });
          return;
        }
        if (subjectSemestersLoading) {
          const msg = "Loading semesters from Academic Info. Please try again in a moment.";
          setError(msg);
          setSnackbar({ open: true, message: msg, severity: "info" });
          return;
        }
        if (!allowedSemesterTokens.size) {
          const msg = "No semesters found in Academic Info. Please add semesters first.";
          setError(msg);
          setSnackbar({ open: true, message: msg, severity: "warning" });
          return;
        }
      }

      const formData = new FormData();
      formData.append("file", file);
      // Determine actual entity type (support 'users' wrapper)
      const actualEntityType =
        entityType === "users" ? mapUserTypeToEntityType(userType) || "" : entityType;

      if (config.requiresParent && parentEntity) {
        formData.append("instituteId", parentEntity);
      }
      // include departmentId for relevant imports (not student — class comes from each CSV row)
      if (
        (effectiveType === "teachers" ||
          effectiveType === "courses" ||
          effectiveType === "subjects" ||
          effectiveType === "classes") &&
        departmentId
      ) {
        formData.append('departmentId', departmentId);
      }
      // teacher dropdown removed; backend will resolve teacher from CSV if provided

      const response = await previewMutation.mutateAsync({
        entityType: actualEntityType,
        formData,
      });

      const data = response.data || {};
      const apiValidRows = data.validRows || data.valid_rows || data.valid || [];
      const apiInvalidRows = data.invalidRows || data.failedRows || data.invalid_rows || [];

      const semesterValidation = validateSubjectSemesters(apiValidRows);
      const mergedInvalidRows = [...apiInvalidRows, ...semesterValidation.invalidRows];
      const finalValidRows = semesterValidation.validRows;
      const totalRows =
        data?.summary?.totalRows ??
        data?.summary?.total ??
        finalValidRows.length + mergedInvalidRows.length;

      const normalizedPreview = {
        ...data,
        validRows: finalValidRows,
        invalidRows: mergedInvalidRows,
        failedRows: mergedInvalidRows,
        summary: {
          ...(data.summary || {}),
          totalRows,
          total: totalRows,
          successCount: finalValidRows.length,
          success: finalValidRows.length,
          failedCount: mergedInvalidRows.length,
          failed: mergedInvalidRows.length,
        },
      };

      setPreviewData(normalizedPreview);
      setActiveStep(1); // Move to preview step
      if (effectiveType === "subjects" && semesterValidation.invalidRows.length > 0) {
        setSnackbar({
          open: true,
          message: `${semesterValidation.invalidRows.length} row(s) failed semester validation.`,
          severity: "warning",
        });
      } else {
        setSnackbar({ open: true, message: "File preview generated", severity: "success" });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "An error occurred while previewing the file";
      setError(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
      console.error("Preview error:", err);
    }
  };

  const handleImport = async () => {
    if (isFileProcessing) return;
    setError(null);
    if (effectiveType === 'courses' && parentEntity && !departmentId) {
      setError('Please select a Department before importing');
      setSnackbar({ open: true, message: 'Please select a Department before importing', severity: 'warning' });
      return;
    }
    setActiveStep(2); // Move to import step (final step)
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      // use actual entity type for users wrapper
      const actualEntityType =
        entityType === "users" ? mapUserTypeToEntityType(userType) || "" : entityType;

      if (config.requiresParent && parentEntity) {
        formData.append("instituteId", parentEntity);
      }
      if ((effectiveType === "teachers" || effectiveType === "courses") && departmentId) {
        formData.append('departmentId', departmentId);
      }
      // teacher dropdown removed; backend will resolve teacher from CSV if provided

      const response = await importMutation.mutateAsync({
        entityType: actualEntityType,
        formData,
      });

      const data = response.data;
      // Normalize response shape so UI can render consistently
      const normalized = {
        summary: data.summary || data || { totalRows: 0, successCount: 0, failedCount: 0 },
        // some endpoints return `failedRows` while others return `invalidRows`
        failedRows: data.failedRows || data.invalidRows || data.invalid_rows || [],
        validRows: data.validRows || data.valid_rows || data.valid || [],
        raw: data,
      };

      setImportResults(normalized);
      // stay on final step and render results
      setSnackbar({
        open: true,
        message: `Import completed: ${normalized.summary?.successCount || normalized.summary?.success || 0} / ${normalized.summary?.totalRows || normalized.summary?.total || 0}`,
        severity: normalized.summary?.failedCount > 0 ? "warning" : "success",
      });
      if (onImportComplete) {
        onImportComplete(data);
      }
      
      // Invalidate relevant queries to refresh dropdown data
      if (actualEntityType === 'departments') {
        queryClient.invalidateQueries({ queryKey: ['departments', 'names', parentEntity] });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "An error occurred during import";
      setError(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
      setActiveStep(1); // Go back to preview
      console.error("Import error:", err);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleDownloadTemplate = async () => {
    try {
      // If importing 'users', require selecting a `userType` first
      const effectiveEntityForDownload = entityType || localEntityType;
      if (effectiveEntityForDownload === "users" && !userType) {
        setSnackbar({
          open: true,
          message: "Please select a user type first",
          severity: "warning",
        });
        return;
      }

      const actualEntityType =
        entityType === "users"
          ? mapUserTypeToEntityType(userType) || ""
          : entityType || localEntityType;

      // Build params from selected dropdowns so backend can omit those columns in the template
      const params = {};
      if (parentEntity) params.instituteId = parentEntity;
      if (departmentId) params.departmentId = departmentId;
      const tplEntity = actualEntityType || entityType;
      if (classId && tplEntity !== "students") params.classId = classId;
      if (practicalBatchCode && tplEntity !== "students") params.practicalBatchCode = practicalBatchCode;

      // Allow downloading classes/courses template without selecting teacher
      // (users can provide Teacher name in the template instead)

      const response = await downloadTemplateMutation.mutateAsync({ entityType: actualEntityType || entityType, params });
      const base = (actualEntityType || entityType) || "import";
      const hdrs = response?.headers || {};
      const dispositionName = (h) => {
        const raw = h?.["content-disposition"] || h?.["Content-Disposition"];
        if (!raw || typeof raw !== "string") return null;
        const m = /filename\*=UTF-8''([^;\s]+)|filename="([^"]+)"|filename=([^;\s]+)/i.exec(raw);
        if (!m) return null;
        return decodeURIComponent((m[1] || m[2] || m[3] || "").replace(/"/g, "")).replace(/^.*\//, "");
      };
      const payload = response?.data !== undefined ? response.data : response;
      let blob;
      let fileName;
      if (payload && typeof payload === "object" && !(payload instanceof Blob) && typeof payload.csvData === "string") {
        blob = new Blob([payload.csvData], { type: "text/csv;charset=utf-8" });
        const fromServer = payload.fileName;
        fileName = fromServer && /\.csv$/i.test(fromServer) ? fromServer : `${base}_import_template.csv`;
      } else if (typeof payload === "string") {
        blob = new Blob([payload], { type: "text/csv;charset=utf-8" });
        fileName = `${base}_import_template.csv`;
      } else if (payload instanceof Blob) {
        const disp = dispositionName(hdrs);
        const ct = String(hdrs["content-type"] || payload.type || "").toLowerCase();
        const peek = new Uint8Array(await payload.slice(0, 2).arrayBuffer());
        const isZip = peek[0] === 0x50 && peek[1] === 0x4b;
        blob = payload;
        if (disp && /\.[a-z0-9]+$/i.test(disp)) fileName = disp;
        else if (isZip || ct.includes("spreadsheet") || ct.includes("officedocument")) fileName = `${base}_import_template.xlsx`;
        else fileName = `${base}_import_template.csv`;
      } else {
        throw new Error("Unexpected template response");
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download template:", err);
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage || "Failed to download template. Please try again.");
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleNext = () => {
    if (activeStep === 1 && previewData) {
      handleImport();
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderUploadStep();
      case 1:
        return renderPreviewStep();
      case 2:
        // final step: show importing progress while running, otherwise show results
        if (importResults) return renderCompleteStep();
        return renderImportingStep();
      default:
        return null;
    }
  };

  const renderUploadStep = () => (
    <Box>
      {/* If the wizard was opened without an entityType prop, let user pick the type */}
      {!entityType && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="select-import-type-label">Import Type</InputLabel>
          <Select
            labelId="select-import-type-label"
            value={localEntityType}
            label="Import Type"
            onChange={(e) => {
              setLocalEntityType(e.target.value);
              setParentEntity(instituteId || "");
              setDepartmentId("");
              setUserType("");
            }}
            disabled={isFileProcessing}
          >
            <MenuItem value=""><em>Select type</em></MenuItem>
            {Object.keys(entityConfig).map((k) => (
              <MenuItem key={k} value={k}>{entityConfig[k].title || k}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* User type selection when importing generic 'users' — shown FIRST before anything else */}
      {entityType === 'users' && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="user-type-label">User Type</InputLabel>
          <Select
            labelId="user-type-label"
            value={userType}
            label="User Type"
            onChange={(e) => setUserType(e.target.value)}
            disabled={isFileProcessing}
          >
            <MenuItem value="">Select User Type</MenuItem>
            {allowedUserTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* If entityType is 'users' and no user type selected yet, show a prompt and hide the rest */}
      {entityType === 'users' && !userType ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          Please select a User Type above to continue with the import.
        </Alert>
      ) : (
      <>
      {/* Template Download */}
      <Alert severity="info" icon={<FileDownload />} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            Download the template file, fill in your data, and upload it here.
          </Typography>
          <Button
            size="small"
            startIcon={<FileDownload />}
            onClick={handleDownloadTemplate}
            disabled={isFileProcessing || downloadTemplateMutation.isPending}
            sx={{ ml: 2 }}
          >
            {downloadTemplateMutation.isPending ? "Downloading..." : "Download Template"}
          </Button>
        </Box>
        {Array.isArray(config.requiredFields) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Required columns: {effectiveRequiredFields.length ? effectiveRequiredFields.join(", ") : 'None (all required values provided via dropdown)'}
          </Typography>
        )}
        {Array.isArray(config.optionalImportColumns) && config.optionalImportColumns.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Optional columns: {config.optionalImportColumns.join(", ")}.{" "}
            {config.optionalImportHint ||
              "The downloaded template includes these headers where applicable."}
          </Typography>
        )}
      </Alert>

      {/* Combined selects: Institute, Department, Teacher - render inline when multiple visible */}
      {
        (() => {
          const showInstitute = config.requiresParent && !instituteId && !["institute", "admin"].includes(currentUserRole);
          const showDepartment = ['teachers', 'courses'].includes(effectiveType);
          const showTeacher = false;

          if (!showInstitute && !showDepartment && !showTeacher) return null;

          const visibleCount = [showInstitute, showDepartment, showTeacher].filter(Boolean).length;
          const multi = visibleCount > 1;

          const formFlex = (count) => {
            if (count === 1) return '1 1 100%';
            if (count === 2) return '1 1 calc(50% - 12px)';
            return '0 0 calc(33.333% - 12px)';
          };

          return (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: multi ? 'nowrap' : 'wrap', alignItems: 'flex-start', overflowX: multi ? 'auto' : 'visible' }}>
              {showInstitute && (
                <FormControl sx={{ flex: formFlex(visibleCount), minWidth: multi ? 200 : 0 }}>
                  <InputLabel>Select Institute</InputLabel>
                  <Select
                    value={parentEntity}
                    onChange={(e) => setParentEntity(e.target.value)}
                    label="Select Institute"
                    disabled={institutesLoading || isFileProcessing}
                  >
                    <MenuItem value="">
                      <em>Select Institute</em>
                    </MenuItem>
                    {institutes.map((institute) => (
                      <MenuItem key={institute.id} value={institute.id}>
                        {institute.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {institutesLoading && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Loading institutes...
                    </Typography>
                  )}
                  {institutesError && (
                    <Typography variant="caption" color="error.main" sx={{ mt: 1 }}>
                      Failed to load institutes: {institutesError.message}
                    </Typography>
                  )}
                  {!institutesLoading && !institutesError && institutes.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      No institutes available
                    </Typography>
                  )}
                </FormControl>
              )}

              {showDepartment && (
                <FormControl required sx={{ flex: formFlex(visibleCount), minWidth: multi ? 200 : 0 }}>
                  <InputLabel>Department</InputLabel>
                    <Select
                      value={departmentId}
                      onChange={(e) => setDepartmentId(e.target.value)}
                      label="Department"
                      disabled={departmentsLoading || !parentEntity || isFileProcessing}
                    >
                    <MenuItem value="">
                      <em>Select Department</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {departmentsLoading && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Loading departments...
                    </Typography>
                  )}
                  {!parentEntity && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Select an institute first
                    </Typography>
                  )}
                  {!departmentsLoading && parentEntity && departments.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      No departments available for selected institute
                    </Typography>
                  )}
                </FormControl>
              )}

                  {/* teacher dropdown removed */}
            </Box>
          );
        })()
      }

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} aria-live="assertive">
          {error}
        </Alert>
      )}

      {/* File Upload */}
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: "center",
          cursor: isFileProcessing ? "not-allowed" : "pointer",
          backgroundColor: selectedFile ? "#f0f7ff" : "#fafafa",
          borderStyle: "dashed",
          borderWidth: 2,
          borderColor: selectedFile ? "primary.main" : "grey.300",
          opacity: isFileProcessing ? 0.65 : 1,
          pointerEvents: isFileProcessing ? "none" : "auto",
          "&:hover": isFileProcessing
            ? {}
            : {
                borderColor: "primary.main",
                backgroundColor: "#f5f5f5",
              },
        }}
        onClick={() => {
          if (isFileProcessing) return;
          if ((effectiveType === 'teachers' || effectiveType === 'courses') && parentEntity && !departmentId) {
            setError('Please select a Department before uploading');
            return;
          }
          fileInputRef.current?.click();
        }}
      >
        <input
          ref={fileInputRef}
          id="file-input"
          type="file"
          accept={config.acceptedFiles}
          onChange={handleFileSelect}
          disabled={isFileProcessing}
          style={{ display: "none" }}
        />
        <CloudUpload sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isFileProcessing
            ? previewMutation.isPending
              ? "Processing file..."
              : "Import in progress..."
            : selectedFile
              ? selectedFile.name
              : "Click to upload file"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isFileProcessing
            ? "Please wait — file selection is disabled until processing completes."
            : "Supported formats: CSV, Excel (.xlsx, .xls)"}
        </Typography>
      </Paper>

      {/* Loading */}
      {isFileProcessing && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
            {importMutation.isPending
              ? "Importing data, please wait..."
              : "Validating file, please wait..."}
          </Typography>
        </Box>
      )}
      </>
      )}
    </Box>
  );

  const formatImportErrorLines = (item) => {
    if (item?.errors && Array.isArray(item.errors) && item.errors.length > 0) {
      return item.errors
        .map((e) => {
          const col = e.column || e.field || "Error";
          const msg = e.message || e.error || e.reason || "Validation failed";
          return `${col}: ${msg}`;
        })
        .filter(Boolean);
    }
    return [item?.reason || item?.error || "Validation failed"].filter(Boolean);
  };

  const renderPreviewStep = () => {
    // Get all unique field names from the data
    const getAllFields = () => {
      const fields = new Set();
      previewData?.validRows?.forEach(row => {
        Object.keys(row).forEach(key => fields.add(key));
      });
      previewData?.invalidRows?.forEach(item => {
        Object.keys(item.row).forEach(key => fields.add(key));
      });
      return Array.from(fields);
    };

    const allFields = getAllFields();

    return (
      <Box>
        {previewData && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">Preview Results</Typography>
              <Box>
                <Chip
                  label={`${previewData.summary.successCount} Valid`}
                  color="success"
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={`${previewData.summary.failedCount} Invalid`}
                  color="error"
                  size="small"
                />
              </Box>
            </Box>

            {previewData.summary.failedCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {previewData.summary.failedCount} records have errors. Only valid records will be imported.
              </Alert>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    {allFields.map(field => (
                      <TableCell key={field} sx={{ fontWeight: 600 }}>
                        {field}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Valid Rows */}
                  {previewData.validRows?.slice(0, 5).map((row, idx) => (
                    <TableRow key={`valid-${idx}`}>
                      <TableCell>
                        <Chip icon={<CheckCircle />} label="Valid" color="success" size="small" />
                      </TableCell>
                      {allFields.map(field => (
                        <TableCell key={field}>
                          <Typography variant="body2">
                            {(() => {
                              const v = row[field];
                              if (v == null || v === "") return "-";
                              return typeof v === "object" ? JSON.stringify(v) : String(v);
                            })()}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell>
                        <Typography variant="body2" color="success.main">
                          Ready to import
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Invalid Rows */}
                  {previewData.invalidRows?.slice(0, 5).map((item, idx) => (
                    <TableRow key={`invalid-${idx}`}>
                      <TableCell>
                        <Chip icon={<Error />} label="Invalid" color="error" size="small" />
                      </TableCell>
                      {allFields.map(field => (
                        <TableCell key={field}>
                          <Typography variant="body2">
                            {(() => {
                              const v = item.row?.[field];
                              if (v == null || v === "") return "-";
                              return typeof v === "object" ? JSON.stringify(v) : String(v);
                            })()}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setSelectedErrorDetail(item);
                            setErrorDialogOpen(true);
                          }}
                          sx={{ whiteSpace: "nowrap", minWidth: "max-content" }}
                        >
                          View details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {(previewData.validRows?.length > 5 || previewData.invalidRows?.length > 5) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Showing first 5 rows of each type. Total: {previewData.summary.totalRows} rows
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderImportingStep = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Importing Data...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait while we import your data. This may take a few moments.
      </Typography>
    </Box>
  );

  const renderCompleteStep = () => {
    const getResultFields = () => {
      const fields = new Set();
      (importResults?.validRows || []).forEach((row) => {
        if (row && typeof row === "object") {
          Object.keys(row).forEach((key) => fields.add(key));
        }
      });
      (importResults?.failedRows || []).forEach((item) => {
        const row = item?.row && typeof item.row === "object" ? item.row : item;
        if (row && typeof row === "object") {
          Object.keys(row).forEach((key) => fields.add(key));
        }
      });
      return Array.from(fields);
    };

    const formatCellValue = (value) => {
      if (value === null || value === undefined || value === "") return "-";
      return typeof value === "object" ? JSON.stringify(value) : String(value);
    };

    const resultFields = getResultFields();

    return (
    <Box>
      <Alert
        severity={importResults?.summary.failedCount === 0 ? "success" : "warning"}
        sx={{ mb: 3 }}
      >
        <Typography variant="h6" gutterBottom>
          Import Complete!
        </Typography>
        <Typography variant="body2">
          Successfully imported {importResults?.summary.successCount} out of{" "}
          {importResults?.summary.totalRows} records
          {importResults?.summary.failedCount > 0 &&
            ` (${importResults.summary.failedCount} failed)`}
        </Typography>
      </Alert>

      {importResults?.validRows?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" gutterBottom>
              Successful Records
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                try {
                  const rows = importResults?.validRows || [];
                  if (!rows.length) return;
                  const headers = Array.from(
                    new Set(rows.flatMap((r) => (r && typeof r === "object" ? Object.keys(r) : [])))
                  );

                  const csvRows = rows.map((r) =>
                    headers
                      .map((h) => {
                        const raw = r?.[h];
                        const value =
                          raw === null || raw === undefined
                            ? ""
                            : typeof raw === "object"
                              ? JSON.stringify(raw)
                              : String(raw);
                        return `"${value.replace(/"/g, '""')}"`;
                      })
                      .join(",")
                  );

                  const csvContent = [
                    `Import Success Report`,
                    `Type: ${effectiveType || entityType || "unknown"}`,
                    `Total Success: ${importResults?.summary?.successCount ?? rows.length}`,
                    "",
                    headers.join(","),
                    ...csvRows,
                  ].join("\n");

                  const blob = new Blob([csvContent], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `import_success_${effectiveType || entityType || "report"}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  // If CSV generation fails, silently ignore (user still has the on-screen list).
                }
              }}
            >
              Download Success Report
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 260 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {resultFields.map((field) => (
                    <TableCell key={`success-header-${field}`}>{field}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {importResults.validRows.slice(0, 10).map((item, idx) => (
                  <TableRow key={idx}>
                    {resultFields.map((field) => (
                      <TableCell key={`success-${idx}-${field}`}>
                        <Typography variant="body2" noWrap>
                          {formatCellValue(item?.[field])}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {importResults.validRows.length > 10 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Showing first 10 successful records of {importResults.validRows.length}.
            </Typography>
          )}
        </Box>
      )}

      {importResults?.failedRows?.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Failed Records
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {resultFields.map((field) => (
                    <TableCell key={`failed-header-${field}`}>{field}</TableCell>
                  ))}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importResults.failedRows.map((item, idx) => (
                  <TableRow key={idx}>
                    {resultFields.map((field) => (
                      <TableCell key={`failed-${idx}-${field}`}>
                        <Typography variant="body2" noWrap>
                          {formatCellValue(item?.row?.[field])}
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setSelectedErrorDetail(item);
                          setErrorDialogOpen(true);
                        }}
                        sx={{ whiteSpace: "nowrap", minWidth: "max-content" }}
                      >
                        View details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (isFileProcessing) return;
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose?.();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">{config.title || (resolvedEntityKey === 'users' ? 'Import Users' : 'Import Data')}</Typography>
          <IconButton onClick={onClose} size="small" disabled={isFileProcessing}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        {activeStep < steps.length - 1 ? (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            {activeStep > 0 && activeStep < steps.length - 1 && (
              <Button onClick={handleBack} disabled={loading}>
                Back
              </Button>
            )}
            {activeStep === 1 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  !previewData ||
                  isFileProcessing ||
                  ((previewData.summary?.successCount || 0) === 0 &&
                    (previewData.summary?.failedCount || previewData.summary?.failCount || 0) === 0)
                }
                startIcon={
                  importMutation.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CloudUpload />
                  )
                }
              >
                {importMutation.isPending
                  ? "Importing..."
                  : (previewData?.summary?.successCount || 0) > 0
                    ? `Import ${previewData.summary.successCount} Valid Records`
                    : "Record Failed Import"}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
            <Button
              startIcon={<Refresh />}
              onClick={() => {
                resetWizard();
              }}
              disabled={isFileProcessing}
            >
              Import Another File
            </Button>
          </>
        )}
      </DialogActions>
      {/* Error detail dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setErrorDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Error Details</DialogTitle>
        <DialogContent dividers>
          {selectedErrorDetail ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Column errors
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {formatImportErrorLines(selectedErrorDetail).map((line, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{ color: "error.main", fontWeight: 500, mb: 0.75 }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default DataImportWizard;
